/**
 * Meta WhatsApp Embedded Signup — loads the Facebook JS SDK, listens for the
 * WA_EMBEDDED_SIGNUP postMessage event, and launches FB.login() to obtain an
 * authorization code (30s TTL) that must be exchanged server-side immediately.
 * See: https://developers.facebook.com/docs/whatsapp/embedded-signup/implementation/
 */

import {
  classifySignupError,
  EmbeddedSignupError,
  type SignupErrorCode,
  type SignupErrorContext,
} from "./signup-errors";

export {
  EmbeddedSignupError,
  classifySignupError,
  needsSupport,
  retryTargetFor,
  SIGNUP_ERROR_CODES,
} from "./signup-errors";
export type { SignupErrorCode, RetryTarget } from "./signup-errors";

const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";

/**
 * Membuat error terklasifikasi sekaligus mencatat sinyal mentah Meta ke log.
 *
 * Pemisahan ini disengaja: yang dilihat pengguna datang dari next-intl lewat
 * `code`, sedangkan `error_message` dan `error_id` Meta hanya masuk console —
 * berguna saat menelusuri masalah, tapi bukan teks yang membantu pemilik toko.
 */
/**
 * Memetakan event penyelesaian Meta ke jalur yang benar-benar dijalankan.
 * Mengembalikan `undefined` bila event tidak dikenal — lebih baik diam daripada
 * menebak jalur yang salah.
 */
function completedFlowOf(event?: string): CompletedFlow | undefined {
  if (event === SIGNUP_EVENTS.FINISH_COEXISTENCE) return "coexistence";
  if (event === SIGNUP_EVENTS.FINISH || event === SIGNUP_EVENTS.FINISH_ONLY_WABA) return "new";
  return undefined;
}

function failWith(code: SignupErrorCode, context: SignupErrorContext = {}): EmbeddedSignupError {
  const err = new EmbeddedSignupError(code, context);
  console.error("[EmbeddedSignup] onboarding gagal", err.toLogPayload());
  return err;
}

/**
 * Feature type Meta untuk jalur coexistence — menghubungkan nomor yang sudah
 * dipakai di aplikasi WhatsApp Business, bukan nomor baru. Tanpa ini Meta
 * menjalankan onboarding nomor baru dan meminta verifikasi nomor.
 */
export const COEXISTENCE_FEATURE_TYPE = "whatsapp_business_app_onboarding";

/**
 * Event penyelesaian yang dikirim Meta lewat `WA_EMBEDDED_SIGNUP`.
 *
 * Membedakan ketiganya penting karena `FINISH` biasa berarti Meta menjalankan
 * onboarding **nomor baru** — kalau itu yang datang padahal pengguna memilih
 * coexistence, konfigurasi ES-nya yang belum mengaktifkan fitur tersebut.
 */
export const SIGNUP_EVENTS = {
  /** Alur nomor baru selesai, nomor sudah ditambahkan. */
  FINISH: "FINISH",
  /** WABA dibuat tapi nomor belum ditambahkan — tetap jalur nomor baru. */
  FINISH_ONLY_WABA: "FINISH_ONLY_WABA",
  /** Alur coexistence selesai — nomor dari aplikasi HP tertaut. */
  FINISH_COEXISTENCE: "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
  CANCEL: "CANCEL",
} as const;

/** Jalur yang benar-benar diselesaikan Meta — belum tentu sama dengan yang diminta. */
export type CompletedFlow = "new" | "coexistence";

export interface EmbeddedSignupResult {
  code: string;
  wabaId: string;
  businessId?: string;
  phoneNumberId?: string;
  /**
   * Apakah pengguna setuju berbagi riwayat chat. `false` adalah jalur normal,
   * bukan kegagalan — pengguna boleh menolak dan tetap terhubung.
   */
  historyShared?: boolean;
  /** Event mentah dari Meta, bila ada. */
  event?: string;
  /**
   * Jalur yang diselesaikan Meta. `undefined` bila Meta tidak mengirim event —
   * sengaja tidak ditebak, karena menebak salah lebih buruk daripada diam.
   */
  completedFlow?: CompletedFlow;
}

export interface LaunchSignupOptions {
  /** Isi dengan COEXISTENCE_FEATURE_TYPE untuk jalur coexistence; kosongkan untuk nomor baru. */
  featureType?: string;
}

interface SignupData {
  waba_id?: string;
  phone_number_id?: string;
  business_id?: string;
  /** Dikirim Meta pada jalur coexistence saat pengguna memilih berbagi riwayat. */
  share_history?: boolean;
  /** FINISH · CANCEL · ERROR — dipakai untuk mengklasifikasi kegagalan. */
  event?: string;
  current_step?: string;
  error_message?: string;
  error_id?: string;
}

/**
 * Origin yang boleh mengirim payload WA_EMBEDDED_SIGNUP.
 *
 * Sebelumnya pemeriksaannya `event.origin.endsWith("facebook.com")`, yang
 * meloloskan domain apa pun yang kebetulan **berakhiran** string itu —
 * `https://evil-facebook.com` lolos. Penyerang yang menguasai domain semacam
 * itu bisa mengirim `waba_id` pilihannya sendiri ke alur onboarding, dan
 * `waba_id` itulah yang diteruskan ke `POST /waba/connect`.
 *
 * Perbandingan sekarang persis, dan hanya `https` — Meta tidak pernah
 * mengirim dari `http`.
 */
const TRUSTED_ORIGINS: ReadonlySet<string> = new Set([
  "https://www.facebook.com",
  "https://web.facebook.com",
  "https://business.facebook.com",
]);

/**
 * Bagian Facebook JS SDK yang benar-benar dipakai di sini.
 *
 * SDK-nya dimuat dari `connect.facebook.net` saat runtime dan tidak punya
 * paket tipe, jadi bentuknya dideklarasikan sendiri — dibatasi pada yang
 * dipakai, bukan menyalin seluruh permukaan SDK yang tidak diverifikasi.
 */
export interface FbLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}

export interface FbLoginOptions {
  config_id: string;
  response_type: string;
  override_default_response_type: boolean;
  extras: Record<string, unknown>;
}

export interface FacebookSdk {
  init(params: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
  login(callback: (response: FbLoginResponse) => void, options: FbLoginOptions): void;
}

/** `window` dengan bidang yang disuntikkan SDK Facebook. */
export interface FacebookWindow extends Window {
  fbAsyncInit?: () => void;
  FB: FacebookSdk;
}

/**
 * Akses `window` bertipe. Cast-nya terpusat di satu tempat supaya tidak
 * tersebar sebagai `any` di setiap pemakaian.
 */
function fbWindow(): FacebookWindow {
  return window as unknown as FacebookWindow;
}

let sdkPromise: Promise<void> | null = null;

/** Loads connect.facebook.net/en_US/sdk.js and calls FB.init. Idempotent — safe to call repeatedly. */
export function loadFacebookSdk(appId: string, version: string): Promise<void> {
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve) => {
    fbWindow().fbAsyncInit = () => {
      fbWindow().FB.init({ appId, autoLogAppEvents: true, xfbml: true, version });
      resolve();
    };
    const script = document.createElement("script");
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  });
  return sdkPromise;
}

/** Registers the WA_EMBEDDED_SIGNUP window.postMessage listener. Returns an unsubscribe fn. */
export function listenForSignupData(onData: (data: SignupData) => void): () => void {
  const handler = (event: MessageEvent) => {
    if (!TRUSTED_ORIGINS.has(event.origin)) return;
    try {
      const raw = JSON.parse(event.data);
      if (raw?.type !== "WA_EMBEDDED_SIGNUP") return;
      // Meta membungkus field di bawah `data`. Bentuk datar juga diterima agar
      // payload lama dan stub test tetap terbaca.
      const inner = (raw.data ?? raw) as Record<string, unknown>;
      onData({ ...inner, event: raw.event } as SignupData);
    } catch {
      // ignore non-JSON / unrelated messages
    }
  };
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

/**
 * Loads the SDK if needed, opens FB.login Embedded Signup, and resolves with
 * the auth code + WABA ids. Rejects on cancel, missing config, or missing waba_id.
 */
export async function launchEmbeddedSignup(options: LaunchSignupOptions = {}): Promise<EmbeddedSignupResult> {
  const appId = process.env.NEXT_PUBLIC_META_APP_ID;
  const version = process.env.NEXT_PUBLIC_META_API_VERSION || "v23.0";
  const configId = process.env.NEXT_PUBLIC_META_CONFIG_ID;
  const SESSION_INFO_VERSION = process.env.NEXT_PUBLIC_META_SESSION_INFO_VERSION || "3";

  if (!appId || !configId) {
    throw failWith("NOT_CONFIGURED", {
      metaMessage: "NEXT_PUBLIC_META_APP_ID / NEXT_PUBLIC_META_CONFIG_ID kosong",
    });
  }

  await loadFacebookSdk(appId, version);

  return new Promise((resolve, reject) => {
    let signupData: SignupData = {};
    const unsubscribe = listenForSignupData((data) => {
      signupData = data;
    });

    fbWindow().FB.login(
      (response: FbLoginResponse) => {
        unsubscribe();
        const context = {
          metaMessage: signupData.error_message,
          metaErrorId: signupData.error_id,
          currentStep: signupData.current_step,
        };

        if (!response?.authResponse?.code) {
          // Meta tidak memberi kode: klasifikasikan dari sinyal terakhir yang
          // sempat sampai lewat postMessage, bukan sekadar "dibatalkan".
          reject(
            failWith(
              classifySignupError({
                event: signupData.event,
                currentStep: signupData.current_step,
                errorMessage: signupData.error_message,
                errorId: signupData.error_id,
              }),
              context
            )
          );
          return;
        }
        // Pembatalan bisa datang bersamaan dengan kode yang sudah terlanjur
        // diterbitkan. Event yang menentukan, bukan ada-tidaknya kode.
        if (signupData.event === SIGNUP_EVENTS.CANCEL) {
          reject(failWith("CANCELLED", context));
          return;
        }
        if (!signupData.waba_id) {
          reject(failWith("UNKNOWN", { ...context, metaMessage: context.metaMessage ?? "waba_id tidak diterima" }));
          return;
        }

        const completedFlow = completedFlowOf(signupData.event);

        // Coexistence diminta tapi Meta menyelesaikan alur nomor baru: gejala
        // paling mungkin adalah NEXT_PUBLIC_META_CONFIG_ID menunjuk konfigurasi
        // ES yang belum mengaktifkan coexistence (lihat S4-OPS-01).
        //
        // Diperingatkan, bukan ditolak — onboarding-nya sendiri berhasil, dan
        // menggagalkannya berarti membuang koneksi yang sudah jadi.
        if (options.featureType === COEXISTENCE_FEATURE_TYPE && completedFlow === "new") {
          console.warn("[EmbeddedSignup] coexistence diminta, tapi Meta menyelesaikan alur nomor baru", {
            requestedFeatureType: options.featureType,
            event: signupData.event,
          });
        }

        resolve({
          code: response.authResponse.code,
          wabaId: signupData.waba_id,
          businessId: signupData.business_id,
          phoneNumberId: signupData.phone_number_id,
          historyShared: signupData.share_history,
          event: signupData.event,
          completedFlow,
        });
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          // Disyaratkan ES v4. Dibuat bisa ditimpa lewat env karena nilainya
          // ditentukan Meta, bukan oleh kita — kalau Meta menaikkannya, itu
          // perubahan konfigurasi, bukan perubahan kode.
          sessionInfoVersion: SESSION_INFO_VERSION,
          // Hanya dikirim untuk jalur coexistence. Membiarkannya undefined pada
          // jalur nomor baru penting — Meta memperlakukan kehadiran kunci ini
          // sebagai penanda alur, bukan nilainya.
          ...(options.featureType ? { featureType: options.featureType } : {}),
        },
      }
    );
  });
}
